import { ACTIONS, type AgentView } from '@quintessence/core';
import { useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { BRAND } from '../theme';

function useWidth<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    setWidth(Math.round(el.getBoundingClientRect().width));
    const ro = new ResizeObserver(([e]) => setWidth(Math.round(e.contentRect.width)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width] as const;
}

/** Stable pseudo-random number in [0, 1) for a drawn synapse or route. */
function hash01(...parts: (string | number)[]): number {
  let h = 2166136261;
  const s = parts.join(':');
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0) / 4294967296;
}

const curve = (x1: number, y1: number, x2: number, y2: number) => {
  const mx = (x1 + x2) / 2;
  return `C ${mx} ${y1}, ${mx} ${y2}, ${x2} ${y2}`;
};

const spread = (count: number, height: number, pad: number) => {
  const step = (height - pad * 2) / count;
  return Array.from({ length: count }, (_, i) => pad + step * (i + 0.5));
};

const NEGATIVE = '#7f86a8';

/** Shorter names for the narrow layout, where a full label would be cut. */
const SHORT: Record<string, string> = {
  drawdown: 'drawdown',
  bounce: 'lift off low',
  holderGrowth1h: '1h holders',
  holderGrowth24h: '24h holders',
  hourSin: 'time of day',
  hourCos: 'time of day',
  idleHours: 'idle hours',
  curveProgress: 'curve',
  burnedShare: 'supply burned',
};

/**
 * A live drawing of one agent's value network: the market signals it currently
 * weighs most (sized by gradient saliency) flowing through two hidden layers
 * (drawn at reduced scale) to the predicted value of each action, with the
 * chosen action lit. Hovering a signal isolates its synapses.
 */
export function NeuralGraph({ agent }: { agent: AgentView }) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const [focus, setFocus] = useState<number | null>(null);
  const uid = useId().replace(/:/g, '');
  const color = BRAND[agent.id];
  const narrow = width > 0 && width < 560;
  const nIn = narrow ? 6 : 8;
  const hidden = narrow ? [5, 4] : [7, 6];
  const rowH = narrow ? 44 : 48;
  const height = nIn * rowH + 16;
  const labelW = narrow ? 108 : 176;
  const outW = narrow ? 58 : 96;

  const thought = agent.lastThought;
  const inputs = agent.brain.attention.slice(0, nIn);
  const maxW = Math.max(1e-6, ...inputs.map((s) => s.weight));
  const minW = Math.min(...inputs.map((s) => s.weight));
  // Attention is often flat across the top signals; stretch the shown range so ranks read at a glance.
  const rank = (w: number) => (maxW - minW > 1e-6 ? 0.2 + (0.8 * (w - minW)) / (maxW - minW) : 1);
  const scores = ACTIONS.map((a) => thought?.scores.find((s) => s.action === a) ?? null);
  const maxV = Math.max(0.05, ...scores.map((s) => (s?.available ? Math.abs(s.mean) : 0)));
  const chosen = thought ? ACTIONS.indexOf(thought.size) : -1;

  const layout = useMemo(() => {
    if (!width) return null;
    const x0 = labelW + 12;
    const x3 = width - outW - 12;
    const xs = [x0, x0 + (x3 - x0) * 0.34, x0 + (x3 - x0) * 0.67, x3];
    const ys = [spread(nIn, height, 8), spread(hidden[0], height, 8 + rowH * 0.4), spread(hidden[1], height, 8 + rowH * 0.7), spread(ACTIONS.length, height, 8 + rowH)];
    return { xs, ys };
  }, [width, labelW, outW, nIn, height, rowH, hidden[0], hidden[1]]);

  if (!layout) return <div ref={ref} className="ng" style={{ height }} />;
  const { xs, ys } = layout;
  const maxChars = Math.floor((labelW - 18) / 6.4);
  const clip = (s: string) => (s.length > maxChars ? `${s.slice(0, maxChars - 1)}…` : s);
  const name = (feature: string, label: string) => clip(narrow ? (SHORT[feature] ?? label) : label);

  type Syn = { key: string; d: string; w: number; opacity: number };
  const synapses: Syn[] = [];
  for (let i = 0; i < inputs.length; i++) {
    const strength = rank(inputs[i].weight);
    for (let j = 0; j < hidden[0]; j++) {
      const w = hash01(agent.id, 0, i, j) * 2 - 1;
      let opacity = 0.035 + 0.34 * Math.abs(w) ** 1.5 * (0.2 + 0.8 * strength);
      if (focus !== null) opacity = i === focus ? Math.min(0.9, opacity * 2.4 + 0.08) : 0.02;
      synapses.push({ key: `a${i}-${j}`, d: `M ${xs[0]} ${ys[0][i]} ${curve(xs[0], ys[0][i], xs[1], ys[1][j])}`, w, opacity });
    }
  }
  for (let j = 0; j < hidden[0]; j++) {
    for (let k = 0; k < hidden[1]; k++) {
      const w = hash01(agent.id, 1, j, k) * 2 - 1;
      synapses.push({ key: `b${j}-${k}`, d: `M ${xs[1]} ${ys[1][j]} ${curve(xs[1], ys[1][j], xs[2], ys[2][k])}`, w, opacity: 0.035 + 0.2 * Math.abs(w) ** 1.5 });
    }
  }
  for (let k = 0; k < hidden[1]; k++) {
    for (let o = 0; o < ACTIONS.length; o++) {
      const w = hash01(agent.id, 2, k, o) * 2 - 1;
      const lit = o === chosen ? 1.5 : 0.55;
      synapses.push({ key: `c${k}-${o}`, d: `M ${xs[2]} ${ys[2][k]} ${curve(xs[2], ys[2][k], xs[3], ys[3][o])}`, w, opacity: 0.035 + 0.22 * Math.abs(w) * lit });
    }
  }

  // Signal routes: start where attention is, end at the chosen action.
  const routes: string[] = [];
  const routeCount = narrow ? 9 : 14;
  const cum = inputs.reduce<number[]>((acc, s) => [...acc, (acc[acc.length - 1] ?? 0) + s.weight], []);
  const total = cum[cum.length - 1] || 1;
  for (let r = 0; r < routeCount; r++) {
    const u = hash01(agent.id, 'r', r, 0) * total;
    const i = focus ?? Math.max(0, cum.findIndex((c) => c >= u));
    const j = Math.floor(hash01(agent.id, 'r', r, 1) * hidden[0]);
    const k = Math.floor(hash01(agent.id, 'r', r, 2) * hidden[1]);
    const o = chosen >= 0 ? chosen : Math.floor(hash01(agent.id, 'r', r, 3) * ACTIONS.length);
    routes.push(
      `M ${xs[0]} ${ys[0][i]} ${curve(xs[0], ys[0][i], xs[1], ys[1][j])} ${curve(xs[1], ys[1][j], xs[2], ys[2][k])} ${curve(xs[2], ys[2][k], xs[3], ys[3][o])}`,
    );
  }

  const top = inputs
    .slice(0, 3)
    .map((s) => `${s.label} ${(s.weight * 100).toFixed(0)}%`)
    .join(', ');
  const label = `${agent.persona.name}'s value network. Strongest signals: ${top || 'none yet'}. ${chosen >= 0 ? `Chose ${ACTIONS[chosen].toLowerCase()}.` : 'No decision yet.'}`;
  const dur = 3.4;

  return (
    <div ref={ref} className="ng" style={{ height, ['--agent' as string]: color }}>
      <svg width={width} height={height} role="img" aria-label={label}>
        <defs>
          <filter id={`glow-${uid}`} x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="2.6" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        <g fill="none">
          {synapses.map((s) => (
            <path key={s.key} d={s.d} stroke={s.w >= 0 ? color : NEGATIVE} strokeOpacity={s.opacity} strokeWidth={0.6 + 1.1 * Math.abs(s.w)} />
          ))}
        </g>

        {/* Each signal is a short dash travelling the route; a wide faint twin stands in for a blur filter. */}
        <g fill="none" stroke={color}>
          {routes.map((d, r) => {
            const timing = { animationDuration: `${dur}s`, animationDelay: `${-((r * dur) / routeCount)}s` };
            return (
              <g key={r}>
                <path d={d} pathLength={100} className="ng-pulse ng-pulse-halo" style={timing} />
                <path d={d} pathLength={100} className="ng-pulse" style={timing} />
              </g>
            );
          })}
        </g>

        {inputs.map((s, i) => {
          const share = rank(s.weight);
          const y = ys[0][i];
          return (
            <g
              key={s.feature}
              className={`ng-input ${focus === i ? 'focus' : ''}`}
              onPointerEnter={() => setFocus(i)}
              onPointerLeave={() => setFocus(null)}
            >
              <rect x={0} y={y - rowH / 2} width={xs[0] + 12} height={rowH} fill="transparent" />
              {share > 0.6 && <circle cx={xs[0]} cy={y} r={4 + 6 * Math.sqrt(share)} fill="none" stroke={color} strokeOpacity={0.28} />}
              <circle cx={xs[0]} cy={y} r={2.6 + 4.4 * Math.sqrt(share)} fill={color} fillOpacity={0.3 + 0.7 * share} />
              <text x={xs[0] - 16} y={y - 3} textAnchor="end" className="ng-label">
                {name(s.feature, s.label)}
              </text>
              <text x={xs[0] - 16} y={y + 12} textAnchor="end" className="ng-sub">
                {(s.weight * 100).toFixed(1)}%
              </text>
            </g>
          );
        })}

        {[1, 2].map((layer) =>
          ys[layer].map((y, n) => (
            <circle
              key={`h${layer}-${n}`}
              cx={xs[layer]}
              cy={y}
              r={3}
              className="ng-neuron"
              style={{ animationDelay: `${-hash01(agent.id, 'h', layer, n) * 3.6}s` }}
            />
          )),
        )}

        {ACTIONS.map((a, o) => {
          const s = scores[o];
          const y = ys[3][o];
          const isChosen = o === chosen;
          const mag = s?.available ? Math.abs(s.mean) / maxV : 0;
          return (
            <g key={a} className={`ng-output ${isChosen ? 'chosen' : ''} ${s?.available === false ? 'na' : ''}`}>
              {isChosen && <circle cx={xs[3]} cy={y} r={11 + 4 * mag} fill="none" stroke={color} strokeOpacity={0.5} className="ng-ring" />}
              <circle
                cx={xs[3]}
                cy={y}
                r={3.4 + 4 * mag}
                fill={isChosen ? color : 'var(--surface-2)'}
                stroke={isChosen ? color : 'var(--ink-3)'}
                strokeOpacity={isChosen ? 1 : 0.6}
                filter={isChosen ? `url(#glow-${uid})` : undefined}
              />
              <text x={xs[3] + 18} y={y - 3} className="ng-label">
                {a.toLowerCase()}
              </text>
              <text x={xs[3] + 18} y={y + 12} className="ng-sub">
                {!s ? '…' : s.available ? `${s.mean >= 0 ? '+' : '−'}${Math.abs(s.mean).toFixed(2)}` : 'n/a'}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
